import { useEffect, useState } from 'react';
import { format } from 'date-fns';

import Modal from '../../../components/modal/modal.component';
import CustomInput from '../../../components/custom-input/custom-input.component';
import DatePicker from '../../../components/date-picker/date-picker.component';
import { TNController } from '../../../components/toast-notification/toast-notification.component';
import { subscriptionSdk } from '../../../services/subscription.service';
import { validateSchema } from '../../../utils/validate-schema';
import ResponseCodes from '../../../constants/response.codes';
import {
    UpdateSubscriptionForm,
    updateSubscriptionFormSchema,
} from '../../../validations/update-subscription-form.schema';

import styles from './update-subscription-modal.module.scss';

type Props = {
    subscriptionId: number;
    onDone: () => void;
};

const UpdateSubscriptionModal = ({ subscriptionId, onDone }: Props) => {
    const [form, setForm] = useState<UpdateSubscriptionForm | null>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const result = await subscriptionSdk.getSubscriptionByAdmin(
                subscriptionId
            );
            if (cancelled) return;
            if ('error' in result) {
                TNController.show(result.error, 2500, 'error');
                onDone();
                return;
            }
            const sub = result;
            setForm({
                expiresAt: new Date(sub.expiresAt),
                freeTowingServices: String(sub.freeTowingServices ?? 0),
                freeAmbulanceServices: String(sub.freeAmbulanceServices ?? 0),
            });
            setFetching(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [subscriptionId, onDone]);

    const updateField = (
        attr: keyof UpdateSubscriptionForm,
        value: string | Date | null
    ) => {
        setForm((prev) => (prev ? { ...prev, [attr]: value } : prev));
    };

    const onSubmit = async () => {
        if (loading || !form) return;

        setError('');
        const errors = validateSchema(form, updateSubscriptionFormSchema);
        if (errors) return setError([...errors.values()].join('. '));

        setLoading(true);
        try {
            const cast = updateSubscriptionFormSchema.cast(form) as {
                expiresAt: Date;
                freeTowingServices: number;
                freeAmbulanceServices: number;
            };

            const response = await subscriptionSdk.updateSubscription(
                subscriptionId,
                {
                    expiresAt: format(cast.expiresAt, "yyyy-MM-dd'T'23:59:59"),
                    freeTowingServices: cast.freeTowingServices,
                    freeAmbulanceServices: cast.freeAmbulanceServices,
                }
            );

            if (response.code !== ResponseCodes.PROCESS_OK) {
                setError(response.message || 'Error al actualizar');
                return;
            }

            TNController.show('Suscripción actualizada', 2500);
            onDone();
        } finally {
            setLoading(false);
        }
    };

    if (fetching || !form) {
        return (
            <Modal onConfirm={onDone} onDiscard={onDone} confirmText="Cerrar">
                <div className={styles.modal}>
                    <h4>Actualizar suscripción</h4>
                    <span>Cargando datos...</span>
                </div>
            </Modal>
        );
    }

    return (
        <Modal
            onConfirm={onSubmit}
            onDiscard={onDone}
            confirmLoading={loading}
            confirmText="Guardar cambios"
        >
            <div className={styles.modal}>
                <h4>Actualizar suscripción</h4>
                <div className={styles.row}>
                    <div className={styles.inputWrapper}>
                        <label>Fecha de fin</label>
                        <DatePicker
                            initialDate={form.expiresAt ?? undefined}
                            onDate={(date) => updateField('expiresAt', date)}
                        />
                    </div>
                </div>
                <div className={styles.row}>
                    <div className={styles.inputWrapper}>
                        <label>Servicios de grúa gratis</label>
                        <CustomInput
                            type="number"
                            min={0}
                            width="100%"
                            value={form.freeTowingServices}
                            onChange={(value) =>
                                updateField('freeTowingServices', value)
                            }
                        />
                    </div>
                    <div className={styles.inputWrapper}>
                        <label>Servicios de ambulancia gratis</label>
                        <CustomInput
                            type="number"
                            min={0}
                            width="100%"
                            value={form.freeAmbulanceServices}
                            onChange={(value) =>
                                updateField('freeAmbulanceServices', value)
                            }
                        />
                    </div>
                </div>
                {error && <span className={styles.errorMessage}>{error}</span>}
            </div>
        </Modal>
    );
};

export default UpdateSubscriptionModal;
